export type RetryOptions = {
    retries?: number;
    interval?: number;
    backoffExponent?: number;
};

export class StaleChunkModuleShapeError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'StaleChunkModuleShapeError';
    }
}

// Specific TypeError signatures produced when React.lazy reads `.default` on a
// fulfilled-with-non-module import. Kept narrow so unrelated errors mentioning `default`
// (variable names in stack frames, library code) do not get silently downgraded.
export const STALE_CHUNK_MESSAGE_PATTERNS: readonly RegExp[] = [
    /Cannot read properties of undefined \(reading 'default'\)/, // Chromium
    /Cannot read properties of undefined \(reading '__esModule'\)/, // Chromium, via interop helpers
    /undefined is not an object \(evaluating '[^']*\.(default|__esModule)'\)/, // Safari
    /StaleChunkModuleShapeError/,
];

export const isStaleChunkError = (error: unknown): boolean => {
    if (error instanceof StaleChunkModuleShapeError) return true;
    if (!(error instanceof Error)) return false;
    if (error.name === 'StaleChunkModuleShapeError') return true;
    const stack = error.stack ?? '';
    return STALE_CHUNK_MESSAGE_PATTERNS.some((p) => p.test(error.message) || p.test(stack));
};

// Signatures for a route chunk that never arrived at all — the network/fetch half of the
// problem, as opposed to STALE_CHUNK_MESSAGE_PATTERNS above which covers a chunk that
// arrived with the wrong shape. This is the signature actually seen in prod on partner-web
// (10 events / 5 distinct real sessions in the 30 days to 2026-09-01, on ReferRoute,
// RegisterRoute, ContactRoute and LoginRoute; a further 12 events over the same window were
// Googlebot and HeadlessChrome, so split by @browser.name before quoting a user-impact figure).
export const CHUNK_FETCH_FAILURE_PATTERNS: readonly RegExp[] = [
    // Chromium's wording for a dynamic import whose fetch never completed. Deliberately not
    // anchored: the message arrives both bare and TypeError-prefixed, and isRecoverableChunkError
    // tests the stack as well as the message.
    //
    // Chromium-only is sufficient here and is a considered choice, not an oversight. Every one of
    // the 10 real crashes measured on partner-web in the 30 days to 2026-09-01 was Chrome, and
    // Firefox plus Safari together account for ~490 of ~96,000 partner sessions (0.5%). Firefox
    // and Safari word this failure differently, so if partner traffic ever shifts off Chromium
    // this list needs their phrasings adding -- confirm the exact strings from RUM rather than
    // guessing them. Note the same reasoning gives a different answer on member-web and
    // expert-web, where Mobile Safari is the largest single source of these failures.
    /Failed to fetch dynamically imported module/,
];

// Broader than isStaleChunkError: because partners uses React.lazy rather than
// @loadable/component, a rejected import() is re-thrown during render and lands in the
// ErrorBoundary rather than on window.onerror. So the boundary has to recognise the fetch
// failures too, not just the wrong-shape ones.
export const isRecoverableChunkError = (error: unknown): boolean => {
    if (isStaleChunkError(error)) return true;
    if (!(error instanceof Error)) return false;
    const stack = error.stack ?? '';
    return CHUNK_FETCH_FAILURE_PATTERNS.some((p) => p.test(error.message) || p.test(stack));
};

// A stale chunk can fulfil import() with non-module HTML rather than rejecting. React.lazy
// then crashes on the `.default` read, so reject here instead and let retry() run.
const isValidModuleShape = (resolved: unknown): boolean =>
    resolved != null && (typeof resolved === 'object' || typeof resolved === 'function');

// Based on https://github.com/gregberge/loadable-components/issues/667
// const Example = lazy(() => retry(() => import('./Example'), { retries: 3 }));
export function retry<R>(
    fn: () => Promise<R>,
    { retries = 3, interval = 500, backoffExponent = 1 }: RetryOptions = {}
) {
    return new Promise<R>((resolve, reject) => {
        fn()
            .then((resolved) => {
                if (!isValidModuleShape(resolved)) {
                    throw new StaleChunkModuleShapeError(
                        'Dynamic import resolved with a non-module value — chunk likely stale.'
                    );
                }
                resolve(resolved);
            })
            .catch((error) => {
                const cause = error instanceof StaleChunkModuleShapeError ? 'resolved a stale chunk' : 'failed to load';
                console.warn(`Chunk ${cause}, retrying. Attempts left: ${retries}.`, error);
                setTimeout(() => {
                    if (retries === 1) {
                        reject(error);
                        return;
                    }

                    // Passing on "reject" is the important part. `backoffExponent` must be passed
                    // on too: without it the recursive call falls back to the default of 1, so the
                    // interval multiplies exactly once and then goes flat. routes.tsx asks for
                    // { retries: 3, interval: 500, backoffExponent: 2 }, which should back off
                    // 500 -> 1000 -> 2000, but waited 500 -> 1000 -> 1000 — a third less time for a
                    // transient blip to clear before the route chunk is abandoned.
                    retry(fn, {
                        retries: retries - 1,
                        interval: interval * backoffExponent,
                        backoffExponent,
                    }).then(resolve, reject);
                }, interval);
            });
    });
}
